import db, { bulkUpsert, setLastSync, getLastSync } from './localDb';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API_BASE = `${BACKEND_URL}/api`;

/**
 * SyncService — Bidirectional sync between Dexie (local) and MongoDB (backend).
 *
 * Architecture:
 *   - Offline writes are queued in `pendingActions` with updatedAt timestamps.
 *   - On reconnect, `fullSync()` calls POST /api/sync/full which does:
 *       1. Push:  Processes the queued actions with last-write-wins (LWW).
 *       2. Pull:  Returns only documents that changed since the client's
 *                 per-collection lastSync timestamps (delta sync).
 *   - This replaces the old approach of 18+ individual HTTP requests per sync.
 *   - For the very first sync (no timestamps), a full pull of all data is returned.
 *   - Conflicts are resolved server-side via LWW and removed from the queue.
 *   - Transient errors trigger exponential backoff retry.
 */

const MAX_RETRIES = 5;
const BASE_BACKOFF_MS = 1000;

// ── helpers ──────────────────────────────────────────────

function authHeaders(token) {
  return token
    ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
    : { 'Content-Type': 'application/json' };
}

async function fetchJSON(url, token) {
  const res = await fetch(url, { headers: authHeaders(token) });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

// ── Queue a mutation for later sync ──────────────────────

export async function queueOfflineAction(action) {
  const token = localStorage.getItem('token');
  const now = new Date().toISOString();
  await db.pendingActions.add({
    type: action.type,
    endpoint: action.endpoint,
    body: action.body || null,
    token: token || null,
    queuedAt: now,
    updatedAt: now,
    retries: 0,
  });
}

// ── Collect per-collection lastSync timestamps ───────────

const SYNC_COLLECTIONS = [
  'users', 'modules', 'lessons', 'quizzes',
  'cycles', 'cycleHistory', 'contraceptions', 'contraceptionLogs',
  'userProgress', 'quizSubmissions',
  'notifications', 'reminders',
  'alertSettings', 'customAlerts',
  'partners', 'communityQuestions', 'tips', 'girlsRoomEvents',
  'badges', 'weeklyChallenges', 'rewards', 'leaderboard',
];

async function buildLastSyncMap() {
  const map = {};
  for (const name of SYNC_COLLECTIONS) {
    map[name] = await getLastSync(name); // null if never synced
  }
  return map;
}

// ── Apply pulled changes to Dexie ────────────────────────

const TABLE_MAP = {
  users:              { table: () => db.users,              clearFirst: false },
  cycles:             { table: () => db.cycles,             clearFirst: false },
  cycleHistory:       { table: () => db.cycleHistory,       clearFirst: false },
  contraceptions:     { table: () => db.contraceptions,     clearFirst: false },
  contraceptionLogs:  { table: () => db.contraceptionLogs,  clearFirst: false },
  userProgress:       { table: () => db.userProgress,       clearFirst: false },
  quizSubmissions:    { table: () => db.quizSubmissions,     clearFirst: false },
  notifications:      { table: () => db.notifications,      clearFirst: false },
  reminders:          { table: () => db.reminders,          clearFirst: false },
  alertSettings:      { table: () => db.alertSettings,      clearFirst: false },
  customAlerts:       { table: () => db.customAlerts,       clearFirst: false },
  modules:            { table: () => db.modules,            clearFirst: false },
  lessons:            { table: () => db.lessons,            clearFirst: false },
  quizzes:            { table: () => db.quizzes,            clearFirst: false },
  partners:           { table: () => db.partners,           clearFirst: false },
  communityQuestions:  { table: () => db.communityQuestions, clearFirst: false },
  tips:               { table: () => db.tips,               clearFirst: false },
  girlsRoomEvents:    { table: () => db.girlsRoomEvents,   clearFirst: false },
  badges:             { table: () => db.badges,             clearFirst: true  },
  weeklyChallenges:   { table: () => db.weeklyChallenges,   clearFirst: true  },
  rewards:            { table: () => db.rewards,            clearFirst: true  },
  leaderboard:        { table: () => db.leaderboard,        clearFirst: true  },
};

async function applyPulledChanges(changes, serverTime) {
  if (!changes) return;

  // Handle alertSettings specially — needs userId key
  if (changes.alertSettings?.length) {
    const user = await db.users.toCollection().first();
    const userId = user?.id || 'current';
    for (const setting of changes.alertSettings) {
      await db.alertSettings.put({ userId, ...setting });
    }
    await setLastSync('alertSettings');
  }

  for (const [key, config] of Object.entries(TABLE_MAP)) {
    if (key === 'alertSettings') continue; // handled above
    const docs = changes[key];
    if (!docs || !Array.isArray(docs) || docs.length === 0) continue;

    const table = config.table();
    if (config.clearFirst) {
      await table.clear();
    }
    await bulkUpsert(table, docs);
    await setLastSync(key);
  }
}

// ── Push pending actions (batch) ─────────────────────────

async function pushBatch(token, actions) {
  const batchPayload = actions.map(a => ({
    type: a.type,
    endpoint: a.endpoint,
    body: a.body,
    updatedAt: a.updatedAt,
  }));

  const lastSyncMap = await buildLastSyncMap();

  const res = await fetch(`${API_BASE}/sync/full`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({
      actions: batchPayload,
      lastSync: lastSyncMap,
    }),
  });

  return { res, lastSyncMap };
}

export async function pushPendingActions() {
  const actions = await db.pendingActions.orderBy('queuedAt').toArray();
  if (actions.length === 0) {
    // No pending actions, but still do a delta pull
    return { synced: 0, failed: 0, conflicts: 0, _noPush: true };
  }

  const token = localStorage.getItem('token') || actions[0]?.token;
  if (!token) return { synced: 0, failed: actions.length, conflicts: 0 };

  const eligible = actions.filter(a => (a.retries || 0) < MAX_RETRIES);
  const expired = actions.filter(a => (a.retries || 0) >= MAX_RETRIES);

  for (const action of expired) {
    console.warn(`[Sync] Dropping action after ${MAX_RETRIES} retries:`, action.endpoint);
    await db.pendingActions.delete(action.id);
  }

  if (eligible.length === 0) return { synced: 0, failed: 0, conflicts: 0, _noPush: true };

  try {
    const { res } = await pushBatch(token, eligible);

    if (res.status === 401) {
      console.warn('[Sync] Auth expired, clearing pending actions');
      await db.pendingActions.clear();
      return { synced: 0, failed: eligible.length, conflicts: 0 };
    }

    if (!res.ok) {
      await _incrementRetries(eligible);
      return { synced: 0, failed: eligible.length, conflicts: 0 };
    }

    const data = await res.json();
    const pushResults = data.push?.results || [];

    let synced = 0, failed = 0, conflicts = 0;

    for (let i = 0; i < pushResults.length; i++) {
      const result = pushResults[i];
      const action = eligible[i];
      if (!action) continue;

      switch (result.status) {
        case 'applied':
        case 'skipped':
          await db.pendingActions.delete(action.id);
          synced++;
          break;
        case 'conflict':
          await db.pendingActions.delete(action.id);
          conflicts++;
          break;
        case 'error':
        default:
          await db.pendingActions.update(action.id, { retries: (action.retries || 0) + 1 });
          failed++;
          break;
      }
    }

    for (let i = pushResults.length; i < eligible.length; i++) {
      await db.pendingActions.update(eligible[i].id, { retries: (eligible[i].retries || 0) + 1 });
      failed++;
    }

    // Apply the delta pull that came back in the same response
    if (data.pull?.changes) {
      await applyPulledChanges(data.pull.changes, data.pull.serverTime);
    }

    console.log(`[Sync] Full result: ${synced} synced, ${conflicts} conflicts, ${failed} failed`);
    return { synced, failed, conflicts };
  } catch (error) {
    console.warn('[Sync] Batch push network error:', error.message);
    await _incrementRetries(eligible);
    return { synced: 0, failed: eligible.length, conflicts: 0 };
  }
}

async function _incrementRetries(actions) {
  for (const action of actions) {
    try {
      await db.pendingActions.update(action.id, { retries: (action.retries || 0) + 1 });
    } catch (e) { console.warn('[Sync] Retry update failed:', e.message); }
  }
}

// ── Retry with exponential backoff ───────────────────────

export async function pushWithRetry() {
  let attempt = 0;
  while (attempt < 3) {
    const result = await pushPendingActions();
    if (result.failed === 0) return result;

    attempt++;
    const delay = BASE_BACKOFF_MS * Math.pow(2, attempt);
    console.log(`[Sync] Retrying in ${delay}ms (attempt ${attempt}/3)`);
    await new Promise(r => setTimeout(r, delay));
  }
  return pushPendingActions();
}

// ── Delta pull only (no push) ────────────────────────────

export async function deltaPull(token) {
  if (!token) return;

  const lastSyncMap = await buildLastSyncMap();

  const res = await fetch(`${API_BASE}/sync/pull`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ lastSync: lastSyncMap }),
  });

  if (!res.ok) throw new Error(`Delta pull failed: HTTP ${res.status}`);
  const data = await res.json();

  if (data.changes) {
    await applyPulledChanges(data.changes, data.serverTime);
  }
}

// ── Legacy: Pull all via individual endpoints ────────────
// Kept as fallback for pre-cache on first login (full pull).

export async function pullAllData(token) {
  if (!token) return;
  // Use the delta pull endpoint with no timestamps → returns everything
  try {
    await deltaPull(token);
    return;
  } catch (e) {
    console.warn('[Sync] Delta pull failed, falling back to individual pulls:', e.message);
  }

  // Fallback: individual endpoint pulls
  const fetches = [
    _pullUserProfile(token), _pullModules(token), _pullLessons(token),
    _pullCycle(token), _pullCycleHistory(token), _pullContraception(token),
    _pullContraceptionLogs(token), _pullNotifications(token), _pullReminders(token),
    _pullBadges(), _pullLeaderboard(token), _pullChallenges(token),
    _pullRewards(token), _pullGirlsRoomEvents(), _pullCommunityQuestions(),
    _pullTips(), _pullPartners(), _pullAlertSettings(token), _pullCustomAlerts(token),
  ];
  const settled = await Promise.allSettled(fetches);
  settled.forEach((r, i) => {
    if (r.status === 'rejected') console.warn(`[Sync] Pull #${i} failed:`, r.reason?.message);
  });
}

// ── Individual pull functions (fallback) ─────────────────

async function _pullUserProfile(token) {
  const data = await fetchJSON(`${API_BASE}/users/profile/full`, token);
  if (data.user) {
    await db.users.put({ ...data.user, _stats: data.stats, _badges: data.badges });
    await setLastSync('users');
  }
}
async function _pullModules(token) {
  const data = await fetchJSON(`${API_BASE}/modules`, token);
  if (data.modules) { await bulkUpsert(db.modules, data.modules); await setLastSync('modules'); }
}
async function _pullLessons(token) {
  const modules = await db.modules.toArray();
  for (const mod of modules) {
    try {
      const data = await fetchJSON(`${API_BASE}/modules/${mod.id}/lessons`, token);
      if (data.lessons) await bulkUpsert(db.lessons, data.lessons);
    } catch (e) { console.warn(`[Sync] Lesson pull failed for module ${mod.id}:`, e.message); }
  }
  await setLastSync('lessons');
}
async function _pullCycle(token) {
  const data = await fetchJSON(`${API_BASE}/cycle`, token);
  if (data.cycle) { await db.cycles.put({ ...data.cycle, predictions: data.predictions }); await setLastSync('cycles'); }
}
async function _pullCycleHistory(token) {
  const data = await fetchJSON(`${API_BASE}/cycle/history?months=12`, token);
  if (data.history) { await bulkUpsert(db.cycleHistory, data.history); await setLastSync('cycleHistory'); }
}
async function _pullContraception(token) {
  const data = await fetchJSON(`${API_BASE}/contraception`, token);
  if (data.contraceptions) { await bulkUpsert(db.contraceptions, data.contraceptions); await setLastSync('contraceptions'); }
}
async function _pullContraceptionLogs(token) {
  const data = await fetchJSON(`${API_BASE}/contraception/history?days=90`, token);
  if (data.history) { await bulkUpsert(db.contraceptionLogs, data.history); await setLastSync('contraceptionLogs'); }
}
async function _pullNotifications(token) {
  const data = await fetchJSON(`${API_BASE}/notifications`, token);
  if (data.notifications) { await bulkUpsert(db.notifications, data.notifications); await setLastSync('notifications'); }
}
async function _pullReminders(token) {
  const data = await fetchJSON(`${API_BASE}/reminders`, token);
  if (data.reminders) { await bulkUpsert(db.reminders, data.reminders); await setLastSync('reminders'); }
}
async function _pullBadges() {
  const data = await fetchJSON(`${API_BASE}/badges`);
  if (data.badges) { await bulkUpsert(db.badges, data.badges); await setLastSync('badges'); }
}
async function _pullLeaderboard(token) {
  const data = await fetchJSON(`${API_BASE}/leaderboard`, token);
  if (data.leaderboard) { await db.leaderboard.clear(); await bulkUpsert(db.leaderboard, data.leaderboard); await setLastSync('leaderboard'); }
}
async function _pullChallenges(token) {
  const data = await fetchJSON(`${API_BASE}/challenges/weekly`, token);
  if (data.challenges) { await db.weeklyChallenges.clear(); await bulkUpsert(db.weeklyChallenges, data.challenges); await setLastSync('weeklyChallenges'); }
}
async function _pullRewards(token) {
  const data = await fetchJSON(`${API_BASE}/rewards`, token);
  if (data.rewards) { await db.rewards.clear(); await bulkUpsert(db.rewards, data.rewards); await setLastSync('rewards'); }
}
async function _pullGirlsRoomEvents() {
  const data = await fetchJSON(`${API_BASE}/girls-room/events`);
  if (data.events) { await bulkUpsert(db.girlsRoomEvents, data.events); await setLastSync('girlsRoomEvents'); }
}
async function _pullCommunityQuestions() {
  const data = await fetchJSON(`${API_BASE}/community/questions`);
  if (data.questions) { await bulkUpsert(db.communityQuestions, data.questions); await setLastSync('communityQuestions'); }
}
async function _pullTips() {
  const data = await fetchJSON(`${API_BASE}/tips`);
  if (data.tips) { await bulkUpsert(db.tips, data.tips); await setLastSync('tips'); }
}
async function _pullPartners() {
  const data = await fetchJSON(`${API_BASE}/partners`);
  if (data.partners) { await bulkUpsert(db.partners, data.partners); await setLastSync('partners'); }
}
async function _pullAlertSettings(token) {
  const data = await fetchJSON(`${API_BASE}/alerts/settings`, token);
  if (data.settings) {
    const user = await db.users.toCollection().first();
    const userId = user?.id || 'current';
    await db.alertSettings.put({ userId, ...data.settings });
    await setLastSync('alertSettings');
  }
}
async function _pullCustomAlerts(token) {
  const data = await fetchJSON(`${API_BASE}/alerts/custom`, token);
  if (data.alerts) { await bulkUpsert(db.customAlerts, data.alerts); await setLastSync('customAlerts'); }
}

// ── Full sync: combined push + pull in one round trip ────

export async function fullSync(token) {
  const pending = await db.pendingActions.orderBy('queuedAt').toArray();
  const hasActions = pending.filter(a => (a.retries || 0) < MAX_RETRIES).length > 0;

  if (hasActions) {
    // Combined push + pull via /api/sync/full
    const pushResult = await pushWithRetry();
    return pushResult;
  }

  // No pending actions — just do a delta pull
  await deltaPull(token);
  return { synced: 0, failed: 0, conflicts: 0 };
}
